import React, { useRef, useState, useEffect, useImperativeHandle, useCallback, useId } from 'react';
import type {
  PlayerInfoTabsProps,
  PlayerInfoTabsAPI,
  GetFileInfoResponse,
  GetFileMetadataResponse,
  GetFileUsersResponse,
  HistoryEvent,
} from './types';
import { Tooltip } from '../hooks/useTooltip';
// Side-effect import: installs window.getPlayerOverlayManager.
import '../player-overlays';
import type { PlayerOverlayManager } from '../player-overlays';

declare const $: any;
declare const moment: any;

function formatDate(timestamp: number): string {
  if (!timestamp) return '';
  return new Date(timestamp * 1000).toLocaleString(undefined, {month: "short", weekday: "short", year: "numeric", day: "numeric", hour: "numeric", minute: "2-digit", second: "2-digit"});
}

function formatRelativeDate(timestamp: number, noFutureDates: boolean = false): string {
  if (!timestamp) return '';
  let ms = timestamp * 1000;

  if (noFutureDates) {
      // Clamp to current time so server clock skew doesn't produce
      // future-tense strings like "in a few seconds"
      ms = Math.min(ms, Date.now());
  }

  if (typeof moment !== 'undefined') {
    return moment(new Date(ms)).fromNow();
  }
  return new Date(ms).toLocaleString();
}

function capitalizeFirst(str: string): string {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

const PlayerInfoTabs = React.forwardRef<PlayerInfoTabsAPI, PlayerInfoTabsProps>(
  function PlayerInfoTabs(props, ref) {
    const { fileId, fileTypeId, colId, projectId, isProjectAdmin, canModifyFile, workflows, onWorkflowChange } = props;

    const containerRef = useRef<HTMLDivElement>(null);
    const tabsInitedRef = useRef(false);

    // Generate unique ids per instance so multiple PlayerInfoTabs mounted
    // side-by-side (one per player column) don't collide on `#info-tabs`,
    // `#info-tab-1`..`#info-tab-4`. jQuery UI tabs only needs the anchor
    // hrefs to match the panel ids structurally — the specific strings
    // don't matter, so useId() is safe.
    // Note: React's useId() returns ids containing ':' which are not valid
    // in CSS selectors (and would need escaping in jQuery selectors), so
    // sanitize to a selector-safe form.
    const rawId = useId();
    const safeId = rawId.replace(/:/g, '-');
    const tabsRootId = `info-tabs-${safeId}`;
    const infoPanelId = `info-tab-1-${safeId}`;
    const metadataPanelId = `info-tab-2-${safeId}`;
    const usersPanelId = `info-tab-3-${safeId}`;
    const historyPanelId = `info-tab-4-${safeId}`;

    // Information tab — fetched on mount
    const [infoData, setInfoData] = useState<GetFileInfoResponse | null>(null);
    const [infoLoading, setInfoLoading] = useState(false);
    const [infoError, setInfoError] = useState<string | null>(null);

    // History tab — fetched on first activation
    const [historyData, setHistoryData] = useState<HistoryEvent[] | null>(null);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState<string | null>(null);
    const historyLoadedRef = useRef(false);

    // Metadata tab — fetched on first activation
    const [metadataData, setMetadataData] = useState<Record<string, string> | null>(null);
    const [metadataLoading, setMetadataLoading] = useState(false);
    const [metadataError, setMetadataError] = useState<string | null>(null);
    const metadataLoadedRef = useRef(false);

    // Users tab — fetched on first activation
    const [usersData, setUsersData] = useState<GetFileUsersResponse | null>(null);
    const [usersLoading, setUsersLoading] = useState(false);
    const [usersError, setUsersError] = useState<string | null>(null);
    const usersLoadedRef = useRef(false);

    // Tracks which tab is currently visible (0=info, 1=metadata, 2=users, 3=history)
    const [currentTabIndex, setCurrentTabIndex] = useState(0);
    // When a lazy tab is about to become visible, we capture the outgoing panel's
    // height and apply it as min-height to prevent the tab container shrinking to
    // the spinner's size (which would cause scroll position shifts). Cleared once
    // the currently-visible tab finishes loading, with a CSS transition so the
    // shrink back to natural size is smooth.
    const [loadingMinHeight, setLoadingMinHeight] = useState<number | null>(null);

    useImperativeHandle(ref, () => ({
      getContainerElement: () => containerRef.current,
    }), []);

    // Fetch the Information tab on mount and whenever fileId changes
    useEffect(() => {
      let cancelled = false;
      setInfoData(null);
      setInfoError(null);
      setInfoLoading(true);

      // Reset history when file switches
      historyLoadedRef.current = false;
      setHistoryData(null);
      setHistoryError(null);

      // Reset metadata when file switches
      metadataLoadedRef.current = false;
      setMetadataData(null);
      setMetadataError(null);

      // Reset users when file switches
      usersLoadedRef.current = false;
      setUsersData(null);
      setUsersError(null);

      (async () => {
        try {
          const response = await fetch(`/file/${fileId}/info`);
          if (!response.ok) {
            let errorMessage = `${response.status} ${response.statusText}`;
            try {
              const body = await response.json();
              if (body?.error) errorMessage = body.error;
            } catch {
              // not JSON
            }
            throw new Error(errorMessage);
          }
          const data: GetFileInfoResponse = await response.json();
          if (!cancelled) {
            setInfoData(data);
            setInfoLoading(false);
          }
        } catch (err) {
          if (!cancelled) {
            const message = err instanceof Error ? err.message : String(err);
            console.error('PlayerInfoTabs: failed to load file info:', message);
            setInfoError(message);
            setInfoLoading(false);
          }
        }
      })();

      return () => {
        cancelled = true;
      };
    }, [fileId]);

    // Lazy-load handler for the Metadata tab
    const loadMetadata = useCallback(async () => {
      if (metadataLoadedRef.current) return;
      metadataLoadedRef.current = true;
      setMetadataLoading(true);
      setMetadataError(null);

      try {
        const response = await fetch(`/file/${fileId}/metadata`);
        if (!response.ok) {
          let errorMessage = `${response.status} ${response.statusText}`;
          try {
            const body = await response.json();
            if (body?.error) errorMessage = body.error;
          } catch {
            // not JSON
          }
          throw new Error(errorMessage);
        }
        const data: GetFileMetadataResponse = await response.json();
        setMetadataData(data.metadata ?? {});
        setMetadataLoading(false);
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        console.error('PlayerInfoTabs: failed to load metadata:', message);
        setMetadataError(message);
        setMetadataLoading(false);
        metadataLoadedRef.current = false; // allow retry on next activation
      }
    }, [fileId]);

    // Lazy-load handler for the Users tab
    const loadUsers = useCallback(async () => {
      if (usersLoadedRef.current) return;
      usersLoadedRef.current = true;
      setUsersLoading(true);
      setUsersError(null);

      try {
        const response = await fetch(`/file/${fileId}/users`);
        if (!response.ok) {
          let errorMessage = `${response.status} ${response.statusText}`;
          try {
            const body = await response.json();
            if (body?.error) errorMessage = body.error;
          } catch {
            // not JSON
          }
          throw new Error(errorMessage);
        }
        const data: GetFileUsersResponse = await response.json();
        setUsersData(data);
        setUsersLoading(false);
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        console.error('PlayerInfoTabs: failed to load users:', message);
        setUsersError(message);
        setUsersLoading(false);
        usersLoadedRef.current = false; // allow retry on next activation
      }
    }, [fileId]);

    // Lazy-load handler for the History tab
    const loadHistory = useCallback(async () => {
      if (historyLoadedRef.current) return;
      historyLoadedRef.current = true;
      setHistoryLoading(true);
      setHistoryError(null);

      try {
        const response = await $.post('ajax/history.php', { project: projectId, file_id: fileId });
        const parsed = typeof response === 'string' ? JSON.parse(response) : response;
        if (parsed?.error) {
          throw new Error(parsed.error);
        }
        setHistoryData(parsed?.history ?? []);
        setHistoryLoading(false);
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        console.error('PlayerInfoTabs: failed to load history:', message);
        setHistoryError(message);
        setHistoryLoading(false);
        historyLoadedRef.current = false; // allow retry on next activation
      }
    }, [projectId]);

    // Initialize jQuery UI tabs after mount and bind activation listener.
    // Target the container element directly rather than an id selector so
    // we don't depend on CSS-escape rules for the generated id.
    useEffect(() => {
      if (!containerRef.current) return;
      const tabsEl = containerRef.current.querySelector('.player-info-tabs-root');
      if (!tabsEl) return;
      const $tabs = $(tabsEl);
      if ($tabs.length === 0) return;

      if (!tabsInitedRef.current) {
        $tabs.tabs();
        tabsInitedRef.current = true;
      }

      // Before the tab switch happens, capture the outgoing panel's height if
      // the incoming tab will show a spinner. This prevents the container
      // shrinking to the spinner's size and shifting scroll position.
      const handleBeforeActivate = (_event: any, ui: any) => {
        const newPanelId = ui.newPanel?.attr('id');
        let willBeLoading = false;
        if (newPanelId === metadataPanelId && !metadataLoadedRef.current) willBeLoading = true;
        else if (newPanelId === usersPanelId && !usersLoadedRef.current) willBeLoading = true;
        else if (newPanelId === historyPanelId && !historyLoadedRef.current) willBeLoading = true;

        if (willBeLoading && ui.oldPanel?.length) {
          const h = ui.oldPanel.outerHeight();
          if (h && h > 0) setLoadingMinHeight(h);
        }
      };

      const handleActivate = (_event: any, ui: any) => {
        // ui.newPanel is a jQuery object for the activated content div
        const panelId = ui.newPanel?.attr('id');
        if (panelId === infoPanelId) {
          setCurrentTabIndex(0);
        } else if (panelId === metadataPanelId) {
          setCurrentTabIndex(1);
          loadMetadata();
        } else if (panelId === usersPanelId) {
          setCurrentTabIndex(2);
          loadUsers();
        } else if (panelId === historyPanelId) {
          setCurrentTabIndex(3);
          loadHistory();
        }
      };

      $tabs.on('tabsbeforeactivate', handleBeforeActivate);
      $tabs.on('tabsactivate', handleActivate);
      return () => {
        $tabs.off('tabsbeforeactivate', handleBeforeActivate);
        $tabs.off('tabsactivate', handleActivate);
      };
    }, [loadHistory, loadMetadata, loadUsers, infoPanelId, metadataPanelId, usersPanelId, historyPanelId]);

    // Clear loadingMinHeight when the currently-visible tab has finished loading.
    // This must be keyed on the CURRENTLY VISIBLE tab, not on whichever tab
    // finished — otherwise if the user clicks a second lazy tab while the first
    // is still fetching, the first's completion would prematurely clear the
    // second's min-height before it's done loading.
    useEffect(() => {
      const isCurrentTabLoading =
        (currentTabIndex === 0 && infoLoading) ||
        (currentTabIndex === 1 && metadataLoading) ||
        (currentTabIndex === 2 && usersLoading) ||
        (currentTabIndex === 3 && historyLoading);

      if (!isCurrentTabLoading && loadingMinHeight !== null) {
        setLoadingMinHeight(null);
      }
    }, [currentTabIndex, infoLoading, metadataLoading, usersLoading, historyLoading, loadingMinHeight]);

    // Destroy jQuery UI tabs on unmount so a remount can re-init cleanly
    useEffect(() => {
      return () => {
        const el = containerRef.current?.querySelector('.player-info-tabs-root');
        if (el && tabsInitedRef.current) {
          try { $(el).tabs('destroy'); } catch { /* ignore */ }
        }
      };
    }, []);

    // ── Render helpers ──

    const renderInformationTab = () => {
      if (infoLoading) return <div role="status" aria-live="polite" aria-busy="true" style={{ textAlign: 'center' }}><div className="spinner"></div><span className="sr-only">Loading…</span></div>;
      if (infoError) return <p role="alert" style={{ padding: '15px', color: '#e24444' }}>Failed to load: {infoError}</p>;
      if (!infoData) return null;

      const showDescription = infoData.description && infoData.description.length > 0;

      return (
        <table cellPadding={3} width="100%">
          <tbody>
            {showDescription && (
              <tr>
                <th scope="row" valign="top">Description</th>
                <td dangerouslySetInnerHTML={{ __html: infoData.description }} />
              </tr>
            )}
            <tr>
                <th scope="row">Date</th>
              <td>{formatDate(infoData.date)}</td>
            </tr>
            {infoData.file_owner.id > 0 && (
              <tr>
                  <th scope="row">Uploaded by</th>
                <td>
                  <a href={`profile?view=${infoData.file_owner.id}`}>
                    <img className="avatar" src={infoData.file_owner.avatar_url} alt={`${infoData.file_owner.first_name} ${infoData.file_owner.last_name} avatar`} />
                    {infoData.file_owner.first_name} {infoData.file_owner.last_name}
                  </a>
                </td>
              </tr>
            )}
            <tr>
                <th scope="row">Views</th>
              <td>
                {infoData.views}
                {infoData.top_viewers.length > 0 && (
                  <span className="inline-viewers" style={{ marginLeft: '8px' }}>
                    {infoData.top_viewers.slice(0, 5).map(user => (
                      <img
                        key={user.id}
                        className="avatar small tooltip"
                        title={`${user.first_name} ${user.last_name}`}
                        src={user.avatar_url}
                        alt={`${user.first_name} ${user.last_name} avatar`}
                      />
                    ))}
                    {infoData.top_viewers.length > 5 && '...'}
                  </span>
                )}
              </td>
            </tr>
            <tr>
              <th scope="row">Downloads</th>
              <td>
                {infoData.downloads}
                {infoData.top_downloaders.length > 0 && (
                  <span className="inline-viewers" style={{ marginLeft: '8px' }}>
                    {infoData.top_downloaders.slice(0, 5).map(user => (
                      <img
                        key={user.id}
                        className="avatar small tooltip"
                        title={`${user.first_name} ${user.last_name}`}
                        src={user.avatar_url}
                        alt={`${user.first_name} ${user.last_name} avatar`}
                      />
                    ))}
                    {infoData.top_downloaders.length > 5 && '...'}
                  </span>
                )}
              </td>
            </tr>
            {infoData.expiration_date > 0 && (
              <tr>
                <th scope="row"><span style={{ color: '#ff5959' }}>Expiration:</span></th>
                <td><span style={{ color: '#ff5959' }}>{formatRelativeDate(infoData.expiration_date, false)}</span></td>
              </tr>
            )}
            <tr>
              <th scope="row">Tags</th>
              <td>
                <div className="tag-view small" style={{flexWrap: 'wrap', gap: '2px'}}>
                  {infoData.tags.map(tag => (
                    <a key={tag} href="#"><div className="tag">{tag}</div></a>
                  ))}
                </div>
                {canModifyFile && (
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      const w = window as any;
                      if (typeof w.showEditFileTagsDialog === 'function') {
                        w.showEditFileTagsDialog([fileId], (file_ids, tags, partials) => finishEditingTagsForFileID(file_ids, tags, partials));
                      }
                    }}
                  >
                    Edit Tags...
                  </a>
                )}
              </td>
            </tr>
            {(canModifyFile || infoData.workflow_id > 0) && (
              <tr>
                <th scope="row">Workflow</th>
                <td>
                  {canModifyFile ? (
                      <select
                        id="player-file-workflow-dropdown"
                        name="workflow"
                        defaultValue={String(infoData.workflow_id)}
                        onChange={(e) => onWorkflowChange?.(parseInt(e.target.value, 10)) }
                      >
                        <option value="-1">(No Workflow)</option>
                        {workflows.map(wf => (
                          <option key={wf.id} value={wf.id}>{wf.name}</option>
                        ))}
                      </select>
                  ) : (
                    workflows.find(wf => wf.id === infoData.workflow_id)?.name ?? '(No Workflow)'
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      );
    };

    const renderMetadataTab = () => {
      if (metadataLoading) return <div role="status" aria-live="polite" aria-busy="true" style={{ textAlign: 'center' }}><div className="spinner"></div><span className="sr-only">Loading…</span></div>;
      if (metadataError) return <p role="alert" style={{ padding: '15px', color: '#e24444' }}>Failed to load: {metadataError}</p>;
      if (!metadataData) return null;

      const entries = Object.entries(metadataData);

      return (
        <>
          {entries.length > 0 ? (
            <table width="90%" className="pagesectable">
                <thead>
                    <tr><th>Key</th><th>Value</th></tr>
                </thead>
                <tbody>
                {entries.map(([key, value]) => (
                  <tr key={key}>
                    <td>{capitalizeFirst(key)}</td>
                    <td>{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p>No metadata exists for this file.</p>
          )}
          <div className="mt-4">
            <a href={`metadata?edit=${fileId}&redir=${encodeURIComponent(`player?id=${fileId}`)}`}>
              <button>Modify Metadata</button>
            </a>
          </div>
        </>
      );
    };

    // Which user's heatmap is currently shown, or -1 for none. Gates the
    // toggle button; the heatmap itself renders in a draggable overlay window
    // over the player (see toggleUserHeatmap), not in the table.
    const [userHeatmapShowId, setUserHeatmapShowId] = React.useState(-1);

    // Resolve THIS column's player stage element. Resolved fresh on each call
    // (not cached) so it stays correct if the stage element is replaced on a
    // version/viewer swap.
    const getHeatmapStage = (): HTMLElement | null =>
        (colId
            ? window.playerColumns?.getColumnStageElement(colId)
            : window.playerColumns?.getActiveStageElement()) ?? null;

    // Resolve the overlay manager for this column's stage. getPlayerOverlayManager
    // de-dupes via a WeakMap, so repeated calls for the same stage share one
    // manager.
    const getHeatmapManager = (): PlayerOverlayManager | null => {
        const stage = getHeatmapStage();
        return stage ? window.getPlayerOverlayManager?.(stage) ?? null : null;
    };

    const toggleUserHeatmap = (user_id: number) => {
        const stage = getHeatmapStage();
        const mgr = stage ? window.getPlayerOverlayManager?.(stage) ?? null : null;
        if (userHeatmapShowId === user_id) {
            setUserHeatmapShowId(-1);
            mgr?.hide('heatmap');
            return;
        }
        setUserHeatmapShowId(user_id);
        const img = document.createElement('img');
        img.src = `/heatmap?file=${fileId}&user=${user_id}`;
        img.className = 'user-heatmap-overlay';
        img.alt = 'Heatmap';
        // Don't let the image start a native drag / be selected when the user
        // drags the overlay window by grabbing it. (CSS also covers this.)
        img.draggable = false;

        // Default the window so its heatmap image spans the full seek-bar width
        // and aligns with it, so the heatmap's x-axis lines up with the timeline.
        // The window is PAD wider on each side than the seek bar so the image
        // (inset by the window padding) matches the seek-bar width exactly; the
        // body is then aligned to the seek bar's left via alignXToReference
        // (the seek bar isn't centered on small players, where the duration
        // label is hidden). scaleMode 'seekbar' keeps it tracking on resize.
        // The heatmap SVG is 960×30 (32:1), so size the height to that aspect.
        const seekBar = stage?.querySelector('.kollab-player-seek-bar') as HTMLElement | null;
        const seekWidth = seekBar?.clientWidth ?? 0;
        const PAD = 12; // matches $overlay-window-base-padding
        const bodyWidth = seekWidth > 0 ? seekWidth : 320;
        const width = bodyWidth + PAD * 2;
        const height = Math.max(Math.round(bodyWidth / 32) + PAD * 2, 32);

        mgr?.show('heatmap', {
            content: img,
            className: 'kollab-heatmap-overlay',
            initialSize: { width, height },
            initialPosition: { x: 0.5, y: 1 }, // y only; x set by alignXToReference
            scaleMode: 'seekbar',
            alignXToReference: true,
            // Audio has no <video>; anchor the overlay to the waveform instead.
            ...(fileTypeId === 2 ? { anchorSelector: '.kollab-player-waveform' } : {}),
            // Keep the toggle button in sync if the user closes via the X.
            onClose: () => setUserHeatmapShowId(-1),
        });
    };

    // Clear the heatmap overlay when the file changes or this tabs instance
    // unmounts, so a heatmap for the previous file doesn't linger over the new
    // one. Reset happens in the effect body (avoids setState during unmount);
    // the cleanup only tears down the overlay. Type-scoped hide('heatmap')
    // leaves any other overlay types intact.
    useEffect(() => {
        setUserHeatmapShowId(-1);
        return () => { getHeatmapManager()?.hide('heatmap'); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileId]);

    const renderUsersTab = () => {
      if (usersLoading) return <div role="status" aria-live="polite" aria-busy="true" style={{ textAlign: 'center' }}><div className="spinner"></div><span className="sr-only">Loading…</span></div>;
      if (usersError) return <p role="alert" style={{ padding: '15px', color: '#e24444' }}>Failed to load: {usersError}</p>;
      if (!usersData) return null;

      const { viewed_users, unviewed_users } = usersData;

      return (
        <div id="receipt_container" className="player_tab_item">
          <a id="users-tab"></a>
          {viewed_users.length > 0 && (
            <>
              <h3>Who viewed this file</h3>
              <table>
                <thead>
                  <tr className="sr-only"><th scope="col">Name</th><th scope="col">Views / Downloads</th><th scope="col">Last viewed</th><th scope="col">Actions</th></tr>
                </thead>
                <tbody>
                  {viewed_users.map(user => (
                      <tr key={user.id}>
                      <td>
                        <a href={`profile?view=${user.id}`}>
                          <img src={user.avatar_url} className="avatar" alt={`${user.name} avatar`} />
                          <span className="avatar">{user.name}</span>
                        </a>
                      </td>
                      <td><span title="Views / Downloads">{user.views} / {user.downloads}</span></td>
                      <td>{user.date > 0 ? formatRelativeDate(user.date, true) : ''}</td>
                      {props.fileTypeId === 1 || props.fileTypeId === 2 ?
                          <td><Tooltip title="Toggle heatmap">
                            <button className={'img-button' + (user.id === userHeatmapShowId ? ' active' : '')} style={{width: '22px', height: '22px'}} onClick={() => toggleUserHeatmap(user.id)} aria-pressed={user.id === userHeatmapShowId}><img src="/img/Heatmaps.svg"/></button>
                        </Tooltip></td>
                      :
                        <td></td>
                      }
                    </tr>
                ))}
                </tbody>
              </table>
            </>
          )}
          {unviewed_users.length > 0 && (
            <>
              <h3 id="unviewed-heading">Unviewed</h3>
              <table>
                  <thead>
                    <tr className="sr-only"><th scope="col">Name</th><th scope="col">Actions</th></tr>
                  </thead>
                <tbody>
                  {unviewed_users.map(user => (
                    <tr key={user.id}>
                      <td>
                        <a href={`profile?view=${user.id}`}>
                          <img src={user.avatar_url} className="avatar" alt={`${user.name} avatar`} />
                          <span className="avatar">{user.name}</span>
                        </a>
                      </td>
                      <td>
                          <Tooltip title="Be alerted when this user views the file">
                            <div
                              className={`user-watch${user.has_receipt ? ' unwatch' : ''}`}
                              data-id={user.id}
                            ></div>
                          </Tooltip>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      );
    };

    const renderHistoryTab = () => {
      if (historyLoading) return <div role="status" aria-live="polite" aria-busy="true" style={{ textAlign: 'center' }}><div className="spinner"></div><span className="sr-only">Loading…</span></div>;
      if (historyError) return <p role="alert" style={{ padding: '15px', color: '#e24444' }}>Failed to load: {historyError}</p>;
      if (!historyData) return null;

      if (historyData.length === 0) {
        return <center>No history found. This file may not have been updated recently.</center>;
      }

      return (
        <>
          <table className="pagesectable" style={{ width: '100%', wordBreak: 'break-word', }}>
            <tbody>
              {historyData.map((event, i) => (
                <tr key={i}>
                  <th scope="row">{event.date}</th>
                  <td dangerouslySetInnerHTML={{ __html: event.description }} />
                </tr>
              ))}
            </tbody>
          </table>
          {projectId > 0 && isProjectAdmin && (
            <p>
              <a href={`history?project=${projectId}`}>
                <button className="actionbtn">View Project History</button>
              </a>
            </p>
          )}
        </>
      );
    };

    const panelStyle: React.CSSProperties = {
      minHeight: loadingMinHeight != null ? `${loadingMinHeight}px` : undefined,
      transition: 'min-height 300ms ease-out',
    };

    const shouldShowInfo = props.tabConfig?.info ?? true;
    const shouldShowMetadata = props.tabConfig?.metadata ?? true;
    const shouldShowUsers = props.tabConfig?.users ?? true;
    const shouldShowHistory = props.tabConfig?.history ?? true;

    return (
      <div ref={containerRef}>
        <div id={tabsRootId} className="player-info-tabs-root tab-view hidden md:block">
          <ul>
              {shouldShowInfo && <li><a href={`#${infoPanelId}`}>Information</a></li>}
              {shouldShowMetadata && <li><a href={`#${metadataPanelId}`}>Metadata</a></li>}
              {shouldShowUsers && <li><a href={`#${usersPanelId}`}>Users</a></li>}
              {shouldShowHistory && <li><a href={`#${historyPanelId}`}>History</a></li>}
          </ul>
            {shouldShowInfo && <div id={infoPanelId} className="info-tab" style={panelStyle}>{renderInformationTab()}</div>}
            {shouldShowMetadata && <div id={metadataPanelId} className="info-tab" style={panelStyle}>{renderMetadataTab()}</div>}
            {shouldShowUsers && <div id={usersPanelId} className="info-tab" style={panelStyle}>{renderUsersTab()}</div>}
            {shouldShowHistory && <div id={historyPanelId} className="info-tab" style={panelStyle}>{renderHistoryTab()}</div>}
        </div>
      </div>
    );
  }
);

export default PlayerInfoTabs;
